import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiX, FiSearch, FiCheck } from 'react-icons/fi';

// =============================================
// Vendors / Customers / Parties › Document types › "Sample receipt" picker.
// A popup: this party's documents first (matched by its name, legal name, nickname or an
// alias — exactly, or as part of the name on the document), a search across every
// document of the account for anything else, and a preview of the highlighted one so it
// can be looked at BEFORE it is chosen. Only the chosen receipt is kept on the line.
// =============================================
/** A dms_documents row (only document_id, filename, party_name, date_of_document,
 *  total_amount are read). */
export type PickerDoc = Record<string, any>;

interface Props {
  documents: PickerDoc[];
  /** the party's names, lower case: name, legal name, nickname, aliases */
  partyNames: string[];
  partyLabel: string;
  value: string;
  onPick: (documentId: string) => void;
  onClose: () => void;
}

const LIST_LIMIT = 300;

export function docLabel(d: PickerDoc): string {
  return [String(d.date_of_document || '').slice(0, 10), d.filename || `Document ${d.document_id}`].filter(Boolean).join(' · ');
}

/** Is this document from the party? Exact name, or the party's name inside the document's
 *  party name (or the other way round) for names of 4+ letters. */
export function isPartyDoc(d: PickerDoc, partyNames: string[]): boolean {
  const p = String(d.party_name || '').trim().toLowerCase();
  if (!p) return false;
  return partyNames.some(n => n === p || (n.length >= 4 && p.length >= 4 && (p.includes(n) || n.includes(p))));
}

const SampleReceiptPicker: React.FC<Props> = ({ documents, partyNames, partyLabel, value, onPick, onClose }) => {
  const own = useMemo(() => documents.filter(d => isPartyDoc(d, partyNames)), [documents, partyNames]);
  const [onlyParty, setOnlyParty] = useState(own.length > 0);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string>(value || (own[0] ? String(own[0].document_id) : ''));
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => { searchRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = onlyParty ? own : documents;
    const hit = needle
      ? base.filter(d => [d.filename, d.party_name, d.date_of_document, d.total_amount, d.document_id]
        .some(v => String(v ?? '').toLowerCase().includes(needle)))
      : base;
    // the party's own documents first, then newest first
    const ownIds = new Set(own.map(d => String(d.document_id)));
    return hit.slice().sort((a, b) =>
      Number(ownIds.has(String(b.document_id))) - Number(ownIds.has(String(a.document_id)))
      || String(b.date_of_document || '').localeCompare(String(a.date_of_document || ''))
      || Number(b.document_id) - Number(a.document_id));
  }, [documents, own, onlyParty, search]);
  const shown = rows.slice(0, LIST_LIMIT);

  // ↑ ↓ move through the list (the preview follows), Enter uses the highlighted receipt.
  const move = (dir: 1 | -1) => {
    if (!shown.length) return;
    const i = shown.findIndex(d => String(d.document_id) === picked);
    const next = shown[Math.min(shown.length - 1, Math.max(0, i < 0 ? 0 : i + dir))];
    setPicked(String(next.document_id));
    listRef.current?.querySelector<HTMLElement>(`[data-doc="${next.document_id}"]`)?.scrollIntoView({ block: 'nearest' });
  };
  const onListKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (e.key === 'Enter' && picked) { e.preventDefault(); onPick(picked); }
  };

  const pickedDoc = documents.find(d => String(d.document_id) === picked);

  return (
    <div className="pm-doc-overlay" role="dialog" aria-modal="true" aria-label="Choose a sample receipt"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pm-doc-modal pm-pick-modal">
        <div className="pm-doc-head">
          <span className="pm-form-title">Choose a sample receipt — {partyLabel || 'this party'}</span>
          <button type="button" className="pm-icon-btn" title="Close" onClick={onClose}><FiX size={18} /></button>
        </div>
        <div className="pm-pick-body">
          <div className="pm-pick-side">
            <div className="pm-pick-search">
              <FiSearch size={14} aria-hidden="true" />
              <input ref={searchRef} className="pm-input" type="text" value={search} placeholder="Search file name, party, date, amount…"
                aria-label="Search documents" onChange={e => setSearch(e.target.value)} onKeyDown={onListKey} />
            </div>
            <label className="pm-pick-only">
              <input type="checkbox" checked={onlyParty} disabled={!own.length} onChange={e => setOnlyParty(e.target.checked)} />
              {own.length
                ? `Only documents from ${partyLabel || 'this party'} (${own.length})`
                : `No documents found under ${partyLabel || 'this name'} — showing all documents`}
            </label>
            <div className="pm-pick-list" ref={listRef} role="listbox" tabIndex={0} aria-label="Documents" onKeyDown={onListKey}
              aria-activedescendant={picked ? `pm-pick-${picked}` : undefined}>
              {shown.length === 0 && <div className="pm-pick-empty">No documents match.</div>}
              {shown.map(d => {
                const id = String(d.document_id);
                return (
                  <div key={id} id={`pm-pick-${id}`} data-doc={id} role="option" aria-selected={id === picked}
                    className={`pm-pick-item ${id === picked ? 'picked' : ''}`}
                    onClick={() => setPicked(id)} onDoubleClick={() => onPick(id)}>
                    <span className="pm-pick-name">{docLabel(d)}</span>
                    <span className="pm-pick-meta">
                      {d.party_name || 'No party'}
                      {d.total_amount != null && d.total_amount !== '' ? ` · ${Number(d.total_amount).toFixed(2)}` : ''}
                      {id === value ? ' · current sample' : ''}
                    </span>
                  </div>
                );
              })}
              {rows.length > LIST_LIMIT && <div className="pm-pick-empty">First {LIST_LIMIT} of {rows.length} — search to narrow.</div>}
            </div>
          </div>
          <div className="pm-pick-preview">
            {picked
              ? <iframe className="pm-doc-frame" title="Receipt preview" src={`/doc-view?doc=${picked}`} />
              : <div className="pm-pick-empty">Click a document to see it here.</div>}
          </div>
        </div>
        <div className="pm-pick-foot">
          {value && (
            <button type="button" className="pm-btn" style={{ marginRight: 'auto' }} onClick={() => onPick('')}>
              Remove sample
            </button>
          )}
          <button type="button" className="pm-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="pm-btn pm-btn-primary" disabled={!picked} onClick={() => onPick(picked)}
            title={pickedDoc ? `Use ${docLabel(pickedDoc)}` : 'Click a document first'}>
            <FiCheck size={15} /> Use this receipt
          </button>
        </div>
      </div>
    </div>
  );
};

export default SampleReceiptPicker;
